<div class="h-full bg-gradient-to-br from-gray-50 to-gray-100 p-6 md:p-8">
	<!-- Header Section -->
	<div class="mb-8">
		<h1 class="text-3xl font-bold text-[#5d202d] mb-2">Testimonials</h1>
		<p class="text-gray-600">Manage customer testimonials and reviews</p>
	</div>

	@if (isLoading()) {
		<!-- Loading State -->
		<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
			@for (i of [1,2,3,4,5,6]; track i) {
				<div class="bg-white rounded-xl p-6 h-48 animate-pulse">
					<div class="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
					<div class="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
					<div class="h-3 bg-gray-200 rounded w-full mb-2"></div>
					<div class="h-3 bg-gray-200 rounded w-full mb-2"></div>
					<div class="h-3 bg-gray-200 rounded w-2/3"></div>
				</div>
			}
		</div>
	} @else if (testimonials().length === 0) {
		<!-- Empty State -->
		<div class="flex flex-col items-center justify-center py-20 bg-white rounded-xl border-2 border-dashed border-gray-300">
			<div class="w-20 h-20 bg-[#5d202d]/10 rounded-full flex items-center justify-center mb-4">
				<svg class="w-10 h-10 text-[#5d202d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
				</svg>
			</div>
			<h3 class="text-xl font-semibold text-gray-900 mb-2">No Testimonials Yet</h3>
			<p class="text-gray-500 mb-6">Start by adding your first customer testimonial</p>
			<button (click)="openModal()" class="px-6 py-3 bg-[#5d202d] text-white rounded-lg font-semibold hover:bg-[#5d202d]/90 transition-colors">
				Add First Testimonial
			</button>
		</div>
	} @else {
		<!-- Testimonials Grid -->
		<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
			@for (testimonial of testimonials(); track testimonial.id) {
				<div class="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 relative overflow-hidden group">
					<!-- Status Badge -->
					<div class="absolute top-4 right-4 flex items-center gap-2">
						<button (click)="toggleActive(testimonial)"
							[class.bg-green-100]="testimonial.is_active"
							[class.text-green-700]="testimonial.is_active"
							[class.bg-gray-100]="!testimonial.is_active"
							[class.text-gray-500]="!testimonial.is_active"
							class="px-3 py-1 rounded-full text-xs font-semibold transition-colors">
							{{ testimonial.is_active ? 'Active' : 'Inactive' }}
						</button>
					</div>

					<!-- Rating Stars -->
					<div class="flex items-center gap-1 mb-4">
						@for (star of [1,2,3,4,5]; track star) {
							<svg [class.text-yellow-400]="star <= testimonial.rating"
								[class.text-gray-300]="star > testimonial.rating"
								class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
							</svg>
						}
					</div>

					<!-- Content -->
					<p class="text-gray-700 text-sm leading-relaxed mb-4 line-clamp-4">{{ testimonial.content }}</p>

					<!-- Author Info -->
					<div class="flex items-center gap-3 mb-6">
						<div class="w-12 h-12 rounded-full bg-[#5d202d]/10 flex items-center justify-center flex-shrink-0">
							<span class="text-[#5d202d] font-bold text-lg">{{ testimonial.name.charAt(0) }}</span>
						</div>
						<div>
							<p class="font-semibold text-gray-900">{{ testimonial.name }}</p>
							<p class="text-sm text-gray-500">{{ testimonial.role }}</p>
						</div>
					</div>

					<!-- Actions -->
					<div class="flex items-center gap-2 pt-4 border-t border-gray-100">
						<button (click)="openModal(testimonial)"
							class="flex-1 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium hover:bg-blue-100 transition-colors flex items-center justify-center gap-2">
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
							</svg>
							Edit
						</button>
						<button (click)="deleteTestimonial(testimonial)"
							class="px-4 py-2 bg-red-50 text-red-700 rounded-lg font-medium hover:bg-red-100 transition-colors">
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
							</svg>
						</button>
					</div>
				</div>
			}
		</div>
	}
</div>

<!-- Modal Template -->
<ng-template #testimonialModalTemplate>
	<div class="bg-white rounded-2xl shadow-2xl w-full">
			<!-- Modal Header -->
			<div class="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-2xl">
				<div class="flex items-center justify-between">
					<h3 class="text-2xl font-bold text-[#5d202d]">
						{{ editingId() ? 'Edit Testimonial' : 'Add New Testimonial' }}
					</h3>
					<button (click)="closeModal()" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
						<svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
						</svg>
					</button>
				</div>
			</div>

			<!-- Modal Body -->
			<div class="px-8 py-6">
				@if (errorMessage()) {
					<div class="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start gap-3">
						<svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
							<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
						</svg>
						<span>{{ errorMessage() }}</span>
					</div>
				}

				<form [formGroup]="testimonialForm" (ngSubmit)="saveTestimonial()" class="space-y-6">
					<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
						<!-- Name -->
						<div>
							<label class="block text-sm font-semibold text-gray-700 mb-2">Customer Name *</label>
							<input type="text" formControlName="name"
								class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all"
								placeholder="John Doe">
						</div>

						<!-- Role -->
						<div>
							<label class="block text-sm font-semibold text-gray-700 mb-2">Role/Occupation</label>
							<input type="text" formControlName="role"
								class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all"
								placeholder="Business Executive">
						</div>
					</div>

					<!-- Content -->
					<div>
						<label class="block text-sm font-semibold text-gray-700 mb-2">Testimonial Content *</label>
						<textarea formControlName="content" rows="4"
							class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all resize-none"
							placeholder="Share the customer's experience..."></textarea>
					</div>

					<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
						<!-- Rating -->
						<div>
							<label class="block text-sm font-semibold text-gray-700 mb-2">Rating *</label>
							<select formControlName="rating"
								class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all">
								<option [value]="5">5 Stars - Excellent</option>
								<option [value]="4">4 Stars - Very Good</option>
								<option [value]="3">3 Stars - Good</option>
								<option [value]="2">2 Stars - Fair</option>
								<option [value]="1">1 Star - Poor</option>
							</select>
						</div>

						<!-- Active Status -->
						<div>
							<label class="block text-sm font-semibold text-gray-700 mb-2">Status</label>
							<div class="flex items-center h-[50px]">
								<label class="relative inline-flex items-center cursor-pointer">
									<input type="checkbox" formControlName="is_active" class="sr-only peer">
									<div class="w-14 h-7 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#5d202d]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
									<span class="ms-3 text-sm font-medium text-gray-700">Active</span>
								</label>
							</div>
						</div>
					</div>

					<!-- Modal Actions -->
					<div class="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
						<button type="button" (click)="closeModal()"
							class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors">
							Cancel
						</button>
						<button type="submit" [disabled]="isSubmitting() || testimonialForm.invalid"
							class="px-6 py-3 bg-[#5d202d] text-white rounded-lg font-semibold hover:bg-[#5d202d]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
							@if (isSubmitting()) {
								<svg class="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
									<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
									<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
								</svg>
								Saving...
							} @else {
								{{ editingId() ? 'Update Testimonial' : 'Add Testimonial' }}
							}
						</button>
					</div>
				</form>
			</div>
	</div>
</ng-template>
